/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars, eqeqeq, no-lone-blocks */
import logger from '../../../utils/logger';
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import {
  getManagedProcesses,
  checkRecordWithGetLinkDraftData,
  checkPublishRecord,
} from '../../../api/api';
import { useTranslation } from '../../../hooks/useTranslation';
import { useProcessNavigation } from '../../../hooks/useProcessNavigation';
import CustomAlert from '../../../components/CustomAlert';

// Material UI Icon for Approval (Red Arrow)
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import { BreadcrumbsContext } from '../../../context/BreadcrumbsContext';
import { buildBreadcrumbs } from '../../../utils/buildBreadcrumbs';

import { formatDate, formatFullDateTime } from '../../../utils/dateUtils';
import { stripHtml } from '../../../components/StripHtml';

const ManagedProcessesWidget = ({ userId }) => {
  const t = useTranslation();
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('process_name'); // Default sort by Process Title
  const [filterText, setFilterText] = useState('');
  const { goToProcess } = useProcessNavigation();

  const { addBreadcrumb } = useContext(BreadcrumbsContext);

  useEffect(() => {
    if (userId) {
      fetchData();
    }
  }, [userId]);

  const fetchData = async () => {
    try {
      const response = await getManagedProcesses({ user_id: userId });

      if (response.status) {
        setProcesses(response.data);
      }
    } catch (error) {
      logger.error('Failed to fetch managed processes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = (event) => {
    setFilterText(event.target.value);
  };

  const filteredProcesses = processes.filter(
    (row) =>
      (row.process_world || '').toLowerCase().includes(filterText.toLowerCase()) ||
      (row.process_name || '').toLowerCase().includes(filterText.toLowerCase())
  );

  const sortedProcesses = filteredProcesses.sort((a, b) => {
    const aValue = (a[orderBy] || '').toString().toLowerCase();
    const bValue = (b[orderBy] || '').toString().toLowerCase();

    if (aValue !== bValue) {
      return order === 'asc' ? (aValue < bValue ? -1 : 1) : aValue > bValue ? -1 : 1;
    }

    // 🔤 Fallback for alphabetical sub-item sorting
    const secondaryKey = orderBy === 'process_name' ? 'process_world' : 'process_name';
    const aSec = (a[secondaryKey] || '').toString().toLowerCase();
    const bSec = (b[secondaryKey] || '').toString().toLowerCase();

    return order === 'asc' ? (aSec < bSec ? -1 : 1) : aSec > bSec ? -1 : 1;
  });

  // Navigation Logic matching Dashboard
  const handleRowClick = async (row, forceDraft = false) => {
    try {
      const Process_id = row.id;
      const user_id = userId;
      const parentNodeId = row.target_node_id; // 👈 parent_id from backend

      const user = { id: user_id, type: 'managed_process_click' };

      // 🧠 Level calculate (same as dashboard)
      // const newLevel = parentNodeId ? 1 : 0;

      const newLevel = parentNodeId?.match(/^level(\d+)/)?.[1]
        ? parseInt(parentNodeId.match(/^level(\d+)/)[1], 10) + 1
        : 0;
      // Level param construction (node based)
      // const levelParam = newLevel === 0
      //     ? "level0"
      //     : `level${newLevel}_${parentNodeId}`;

      const levelParam = `level${newLevel}${parentNodeId ? `_${parentNodeId}` : ''}`;

      const [nodeData, publishdata] = await Promise.all([
        checkRecordWithGetLinkDraftData(levelParam, parseInt(user_id), Process_id, parentNodeId),
        checkPublishRecord(levelParam, Process_id),
      ]);

      if (!nodeData?.status) {
        CustomAlert.warning(
          t('warning') || 'Warning',
          t('first_create_next_model_of_this_existing_model') ||
            'First create next model of this existing model'
        );
        return;
      }

      const processTitle = nodeData.processTitle?.process_title;
      const TitleTranslation = JSON.parse(nodeData.processTitle?.translations || '{}');

      // 🌱 Root breadcrumb
      const state = { Process_id, processTitle, TitleTranslation };
      const isDraftMode = forceDraft || !publishdata.status;
      const basePath = isDraftMode ? `/draft/map/${Process_id}` : `/published/map/${Process_id}`;

      addBreadcrumb(processTitle, basePath, state);

      // 🌳 Child breadcrumbs (same logic as dashboard)
      const breadcrumbs = buildBreadcrumbs(
        nodeData.allNodes,
        nodeData.ids,
        Process_id,
        isDraftMode ? 'draft' : 'Publish'
      );

      breadcrumbs.forEach(({ label, path, state }) => {
        addBreadcrumb(label, path, state);
      });

      // 🚀 Navigation
      const mode = isDraftMode ? 'draft' : 'published';
      const view = nodeData.Page_Title === 'Swimlane' ? 'swimlane' : 'map';

      if (newLevel === 0) {
        goToProcess({
          mode,
          view,
          processId: Process_id,
        });
      } else {
        goToProcess({
          mode,
          view,
          processId: Process_id,
          level: newLevel,
          parentId: parentNodeId,
        });
      }
    } catch (error) {
      logger.error('Managed Process Navigation Error:', error);
    }
  };

  if (!processes.length) return null;

  return (
    <Box
      sx={{
        mt: 1.5,
        border: '1px solid #1A192B',
        borderRadius: '0px',
        overflow: 'hidden',
        boxShadow: '0px 5px 10px #afadad5e',
        className: 'processbox',
      }}
    >
      <Box
        style={{
          borderBottom: '1px solid #1A192B',
          padding: '2.438px 2.438px',
          margin: 0,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: '#002060',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: '12px',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="manageproce_icon"
              viewBox="0 0 640 640"
            >
              <path
                fill="#002060"
                d="M192 64C156.7 64 128 92.7 128 128L128 544C128 555.5 134.2 566.2 144.2 571.8C154.2 577.4 166.5 577.3 176.4 571.4L320 485.3L463.5 571.4C473.4 577.3 485.7 577.5 495.7 571.8C505.7 566.1 512 555.5 512 544L512 128C512 92.7 483.3 64 448 64L192 64z"
              />
            </svg>
          </span>{' '}
          {t('my_managed_processes') || 'My managed Processes'}
        </Typography>

        <TextField
          style={{ border: 'none', outline: 'none' }}
          variant="standard"
          size="small"
          placeholder="Filter..."
          className="filter_box"
          value={filterText}
          onChange={handleFilterChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {/* <SearchIcon fontSize="small" /> */}
              </InputAdornment>
            ),
          }}
          sx={{ width: 200 }}
        />
      </Box>

      <TableContainer component={Paper} elevation={0} style={{ padding: 10 }}>
        <Table
          sx={{ minWidth: 650 }}
          size="small"
          aria-label="managed processes table"
          className="ss_dash_table home_table mainproce"
        >
          <TableHead>
            <TableRow>
              {[
                { id: 'process_world', label: t('Process') || 'Process' }, // Node Label in Process Column
                { id: 'process_name', label: t('process_world') || 'Process World' }, // Root Name in PW Column
                { id: 'roles', label: t('my_bpm_roles') || 'My BPM Roles' },
                { id: 'draft_date', label: t('Draft') },
                { id: 'to_be_published_on', label: t('to_be_published_on') },
                { id: 'published_date', label: t('Published') },
                { id: 'todo', label: t('todo') },
              ].map((headCell) => (
                <TableCell
                  key={headCell.id}
                  sortDirection={orderBy === headCell.id ? order : false}
                  sx={{
                    fontWeight: '400',
                    color: '#002060',
                    fontSize: '12px',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={() => handleRequestSort(headCell.id)}
                  >
                    {headCell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t('loading')}
                </TableCell>
              </TableRow>
            ) : sortedProcesses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t('no_managed_processes_found') || 'No managed processes found.'}
                </TableCell>
              </TableRow>
            ) : (
              sortedProcesses.map((row, index) => (
                <TableRow
                  key={`${row.unique_key || row.id}_${index}`}
                  // hover
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell
                    component="th"
                    scope="row"
                    onClick={() => handleRowClick(row)}
                    sx={{
                      cursor: 'pointer',
                      color: '#002060',
                      fontWeight: '600',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#1a73e8',
                      },
                    }}
                  >
                    {stripHtml(row.process_world)}
                  </TableCell>
                  <TableCell>{stripHtml(row.process_name)}</TableCell>
                  <TableCell>{row.roles.join(', ')}</TableCell>
                  <TableCell>{formatDate(row.draft_date)}</TableCell>
                  <TableCell>{formatFullDateTime(row.to_be_published_on)}</TableCell>
                  <TableCell>{formatFullDateTime(row.published_date)}</TableCell>
                  <TableCell>
                    {row.todo === 'Approval' && (
                      <Box
                        component="span"
                        onClick={() => handleRowClick(row, true)}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: '#ff364a',
                          cursor: 'pointer',
                          fontWeight: '400',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        <ArrowCircleRightIcon sx={{ color: '#ff364a', fontSize: '1.2rem' }} />
                        {t('Approval') || 'Approval'}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ManagedProcessesWidget;
